import type { FabricEditor } from 'modules/fabric-editor';
import {
  applyMatchLayout,
  authoredBundlesBbox,
  collectSurvivingBundles,
  type LayoutScopeKey,
  MatchLayoutSettings,
} from 'modules/manager/domain/match-layout';

/**
 * Reposition the surviving slot bundles inside the area the originally-authored
 * bundles 1..N occupied, per the template's `matchLayout` settings. A "slot" is
 * a match (`scopeKey='matchNumber'`) for match templates or a table row
 * (`scopeKey='teamNumber'`) for standings templates.
 *
 * Runs after `filterScopedObjects` (which removed out-of-range bundles) and
 * before `substitutePlaceholders` (so positions are computed against authored
 * placeholder sizes, not resolved-content sizes — matching what the author saw
 * in the editor).
 *
 * No-op when:
 *  - `settings` is disabled or absent
 *  - the authored DTO has no positively-scoped objects
 *  - `availableCount` is 0
 *  - every authored bundle survived (`availableCount >= authoredCount`)
 *  - no surviving bundles remain on the canvas
 */
export async function centerScopedBundles(
  editor: FabricEditor,
  scopeKey: LayoutScopeKey,
  availableCount: number,
  settings: MatchLayoutSettings | undefined,
  authoredObjects: readonly unknown[] | undefined,
): Promise<void> {
  if (!settings || !settings.enabled) return;
  if (!authoredObjects || authoredObjects.length === 0) return;

  const authoredRegion = authoredBundlesBbox(authoredObjects, scopeKey);
  if (!authoredRegion) return;

  const authoredCount = countAuthoredSlots(authoredObjects, scopeKey);
  if (availableCount === 0) return;
  if (availableCount >= authoredCount) return;

  const surviving = collectSurvivingBundles(editor.canvas.getObjects(), scopeKey);
  if (surviving.length === 0) return;

  applyMatchLayout({ surviving, authoredRegion, settings });
  editor.canvas.requestRenderAll();
}

function countAuthoredSlots(authoredObjects: readonly unknown[], scopeKey: LayoutScopeKey): number {
  let max = 0;
  for (const raw of authoredObjects) {
    const n = (raw as { data?: Record<string, number | undefined> } | undefined)?.data?.[scopeKey];
    if (typeof n === 'number' && n > max) max = n;
  }
  return max;
}
